import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import { fetchHostById } from "../../api/host.api";

const HostDetails = () => {
  const { background } = useContext(ThemeContext);
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const isDark = background === "dark";

  useEffect(() => {
    const fetchHost = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchHostById(id);
        console.log("🚀 ~ fetchHost ~ response:", response);
        setHost(response?.data || response);
      } catch (err) {
        console.error("Error fetching host:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch host details"
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchHost();
  }, [id]);

  const navigateToCreatePage = () => {
    if (!host) return;
    navigate("dome/create", {
      state: {
        hostData:host
      },
    });
  };

  const navigateToHostDomes = () => {
    if (!host) return;

    navigate("dome", {
      state: {
        hostData: host, // send the entire host object
      },
    });
  };

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="container py-5 text-center">
        <h4 className="text-danger mb-3">Error Loading Host</h4>
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="btn btn-primary">
          Go Back
        </button>
      </div>
    );

  if (!host)
    return (
      <div className="container py-5 text-center">
        <h4>No host data found</h4>
        <button onClick={() => navigate(-1)} className="btn btn-primary">
          Go Back
        </button>
      </div>
    );

  const {
    companyName,
    hostDisplayName, // Renamed from companyDisplayName
    domain,
    type,
    primaryEmail,
    primaryPhone,
    website,
    facebookPage,
    instagram,
    tikTok,
    otherSocial,
    onboardingTraining,
    strategyAndAccount,
    technicalServicingNotes,
    latitude,
    longitude,
    billingPhone,
    billingAddress,
    billingAddress2,
    billingCity,
    billingState,
    billingZipCode,
    createdAt,
    updatedAt,
    archived,
    // Fields from Mongoose Schema
    primaryFirstName,
    primaryLastName,
    primaryTitle,
    secondaryEmail,
    secondaryFirstName,
    secondaryLastName,
    secondaryPhone,
    secondaryTitle,
    hostloginmail,
    ATTN,
    otherPOC,
    countryRegionCode,
    shippingAddress,
    shippingAddress2,
    shippingCity,
    shippingState,
    shippingZipCode,
    shippingCountry,
    billingCompanyName,
    billingContact,
    accountOverview,
    transferedFrom,
    transferedTo,
    parentCompany,
    market,
    ownership,
    industry,
    installDate,
    board,
    hardwareScreen,
    domeID,
    audio,
    check,
    broker,
    extras,
    instaFollowing,
    // Notes: parentHost, alternatePhone, billingEmail, billingName, and general address fields (addressLine1, etc.) were removed as they are missing from the schema.
  } = host;

  // Use shipping address fields as a fallback for the main address display if host data doesn't provide a dedicated one.
  const mainAddressLine1 = host.addressLine1 || shippingAddress;
  const mainAddressLine2 = host.addressLine2 || shippingAddress2;
  const mainCity = host.city || shippingCity;
  const mainState = host.state || shippingState;
  const mainZipCode = host.zipCode || shippingZipCode;
  const mainCountry = host.country || shippingCountry;

  const formatAddress = (line1, line2, city, state, zip, country) =>
    [line1, line2, city, state, zip, country].filter(Boolean).join(", ") ||
    "N/A";

  const formattedInstallDate = installDate
    ? new Date(installDate).toLocaleDateString()
    : "N/A";

  return (
    <div style={{ minHeight: "100vh" }}>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h2 className="mb-0">Host Details</h2>

          <div className="d-flex gap-2">
            <button
              onClick={() => navigate("edit")}
              className="btn btn-primary"
            >
              <i className="fas fa-edit me-2"></i>Edit
            </button>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-outline-secondary"
            >
              <i className="fas fa-arrow-left me-2"></i>Back
            </button>
          </div>
        </div>

        {/* Info Card - Updated with Primary Contact details */}
        <div className={`card ${isDark ? "bg-dark" : ""} mb-4`}>
          <div className="card-body position-relative p-4 d-flex flex-wrap align-items-center">
            <div className="me-4 mb-3">
              {/* Note: profileImage was removed from destructuring, defaulting to placeholder */}
              <div
                className="bg-danger rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 100, height: 100 }}
              >
                <i className="fas fa-user fs-1 text-white"></i>
              </div>
            </div>

            <div className="flex-grow-1">
              <h2 className="fs-24 font-w600 mb-1">
                {hostDisplayName || companyName}
              </h2>
              <p className="text-gray mb-2">
                {companyName || domain || "Independent Host"}
              </p>

              {/* Primary Contact Details */}
              {(primaryFirstName || primaryLastName) && (
                <p className="mb-1">
                  <strong>Primary Contact:</strong> {primaryFirstName}{" "}
                  {primaryLastName} ({primaryTitle})
                </p>
              )}

              <p className="mb-1">
                <strong>Email:</strong> {primaryEmail}
              </p>
              <p className="mb-1">
                <strong>Phone:</strong> {primaryPhone || "N/A"}
              </p>
              {/* alternatePhone was removed as it's not in the schema */}
              {hostloginmail && (
                <p className="mb-1">
                  <strong>Login Email:</strong> {hostloginmail}
                </p>
              )}
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-danger mt-2"
                >
                  <i className="fas fa-external-link-alt me-1"></i>Website
                </a>
              )}
              <button
                className="btn btn-sm btn-outline-primary mt-2 mx-1"
                onClick={navigateToHostDomes}
              >
                <i className="fas fa-eye me-1"></i>View Domes
              </button>
              <button
                className="btn btn-sm btn-outline-primary mt-2 mx-1"
                onClick={navigateToCreatePage}
              >
                <i className="fas fa-plus me-1"></i>Create Dome
              </button>
            </div>

            <div
              className="position-absolute"
              style={{ top: "20px", right: "20px" }}
            >
              <span className="badge bg-danger px-3 py-2">ID: {host._id}</span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="row mb-4">
          <div className="col-xl-6 col-lg-6 mb-4">
            <div className={`card h-100 ${isDark ? "bg-dark" : ""}`}>
              <div className="card-header border-0">
                <h4 className="card-title">Timeline</h4>
              </div>
              <div className="card-body">
                <p>
                  <strong>Created:</strong>{" "}
                  {createdAt ? new Date(createdAt).toLocaleString() : "N/A"}
                </p>
                <p>
                  <strong>Last Updated:</strong>{" "}
                  {updatedAt ? new Date(updatedAt).toLocaleString() : "N/A"}
                </p>
                {installDate && (
                  <p>
                    <strong>Install Date:</strong> {formattedInstallDate}
                  </p>
                )}
                {archived !== undefined && (
                  <p>
                    <strong>Status:</strong>{" "}
                    <span className={archived ? "text-danger" : "text-success"}>
                      {archived ? "Archived" : "Active"}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Info - Using best available Address fields */}
          <div className="col-xl-6 col-lg-6 mb-4">
            <div className={`card h-100 ${isDark ? "bg-dark" : ""}`}>
              <div className="card-header border-0">
                <h4 className="card-title">Contact Information</h4>
              </div>
              <div className="card-body">
                {/* Fallback to using shipping address or defaults if main address is missing from schema */}
                <p>
                  <strong>Address:</strong>{" "}
                  {formatAddress(
                    mainAddressLine1,
                    mainAddressLine2,
                    mainCity,
                    mainState,
                    mainZipCode,
                    mainCountry
                  )}
                </p>
                {countryRegionCode && (
                  <p>
                    <strong>Country Code:</strong> {countryRegionCode}
                  </p>
                )}
                {ATTN && (
                  <p>
                    <strong>ATTN:</strong> {ATTN}
                  </p>
                )}
                {latitude && longitude && (
                  <p>
                    <strong>Coordinates:</strong> {latitude}, {longitude}
                  </p>
                )}

                {/* Secondary Contact */}
                {(secondaryEmail || secondaryPhone) && (
                  <>
                    <hr />
                    <h6 className="mt-3">Secondary Contact</h6>
                    {(secondaryFirstName || secondaryLastName) && (
                      <p className="mb-1">
                        <strong>Name:</strong> {secondaryFirstName}{" "}
                        {secondaryLastName} ({secondaryTitle})
                      </p>
                    )}
                    {secondaryEmail && (
                      <p className="mb-1">
                        <strong>Email:</strong> {secondaryEmail}
                      </p>
                    )}
                    {secondaryPhone && (
                      <p className="mb-1">
                        <strong>Phone:</strong> {secondaryPhone}
                      </p>
                    )}
                  </>
                )}

                {/* Other POC */}
                {otherPOC && (
                  <>
                    <hr />
                    <h6 className="mt-3">Other POC</h6>
                    <p>{otherPOC}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Social Links & Billing */}
        <div className="row mb-4">
          {/* Social */}
          <div className="col-xl-6 col-lg-6 mb-4">
            <div className={`card h-100 ${isDark ? "bg-dark" : ""}`}>
              <div className="card-header border-0">
                <h4 className="card-title">Social Links</h4>
              </div>
              <div className="card-body">
                {facebookPage && (
                  <p>
                    <strong>Facebook:</strong>{" "}
                    <a
                      href={facebookPage}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {facebookPage}
                    </a>
                  </p>
                )}
                {instagram && (
                  <p>
                    <strong>Instagram:</strong>{" "}
                    <a
                      href={instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {instagram}
                    </a>
                  </p>
                )}
                {instaFollowing && (
                  <p>
                    <strong>Insta Following:</strong> {instaFollowing}
                  </p>
                )}
                {tikTok && (
                  <p>
                    <strong>TikTok:</strong>{" "}
                    <a href={tikTok} target="_blank" rel="noopener noreferrer">
                      {tikTok}
                    </a>
                  </p>
                )}
                {otherSocial && (
                  <p>
                    <strong>Other:</strong>{" "}
                    <a
                      href={otherSocial}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {otherSocial}
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Billing - Using only schema-present fields */}
          <div className="col-xl-6 col-lg-6 mb-4">
            <div className={`card h-100 ${isDark ? "bg-dark" : ""}`}>
              <div className="card-header border-0">
                <h4 className="card-title">Billing Information</h4>
              </div>
              <div className="card-body">
                {billingCompanyName && (
                  <p>
                    <strong>Company:</strong> {billingCompanyName}
                  </p>
                )}
                {/* billingName was removed, using Company or N/A */}
                <p>
                  <strong>Contact:</strong> {billingContact || "N/A"}
                </p>
                {/* billingEmail was removed */}
                <p>
                  <strong>Phone:</strong> {billingPhone || "N/A"}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  {formatAddress(
                    billingAddress,
                    billingAddress2,
                    billingCity,
                    billingState,
                    billingZipCode,
                    // Note: country is not in the schema, using shippingCountry as proxy if available
                    shippingCountry
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Properties - Using only schema-present fields */}
        <div className="row mb-4">
          <div className="col-12">
            <div className={`card ${isDark ? "bg-dark" : ""}`}>
              <div className="card-header border-0">
                <h4 className="card-title">Other Properties</h4>
              </div>
              <div
                className="card-body"
                style={{ maxHeight: 500, overflowY: "auto" }}
              >
                {/* Shipping Address */}
                {(shippingAddress || shippingCity) && (
                  <>
                    <h5 className="mb-3">Shipping Information</h5>
                    <p className="mb-1">
                      <strong>Address:</strong>{" "}
                      {formatAddress(
                        shippingAddress,
                        shippingAddress2,
                        shippingCity,
                        shippingState,
                        shippingZipCode,
                        shippingCountry
                      )}
                    </p>
                    <hr className="my-3" />
                  </>
                )}

                {/* Main Other Properties Table */}
                <h5 className="mb-3">System & Account Details</h5>
                <table className="table table-borderless">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "Account Overview", value: accountOverview },
                      { key: "Onboarding Training", value: onboardingTraining },
                      { key: "Strategy & Account", value: strategyAndAccount },
                      {
                        key: "Technical Servicing Notes",
                        value: technicalServicingNotes,
                      },
                      // notes field was removed from schema
                      { key: "Type", value: type },
                      { key: "Domain", value: domain },
                      { key: "Industry", value: industry },
                      { key: "Market", value: market },
                      { key: "Ownership", value: ownership },
                      { key: "Dome ID", value: domeID },
                      { key: "Board Version", value: board },
                      { key: "Hardware/Screen", value: hardwareScreen },
                      { key: "Audio Enabled", value: audio },
                      { key: "Check (Boolean)", value: check?.toString() },
                      { key: "Broker", value: broker },
                      { key: "Extras", value: extras },
                      // parentHost was removed from schema
                      { key: "Parent Company", value: parentCompany },
                      { key: "Transfered From", value: transferedFrom },
                      { key: "Transfered To", value: transferedTo },
                    ].map(({ key, value }) => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{value || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostDetails;
